/**
 * Notification Context
 * Provides global notification state management and API
 */

import React, { 
  createContext, 
  useContext, 
  useState, 
  useCallback, 
  useEffect,
  useRef,
  useMemo
} from 'react';

import {
  NotificationObject,
  NotificationConfig,
  NotificationContextValue,
  NotificationProviderProps,
  AnimationState,
} from '../types';

import { NotificationQueueManager } from '../utils/queue-manager';
import { AnimationController } from '../utils/animation-controller';
import {
  createNotification,
  createSuccessNotification,
  createErrorNotification,
  createWarningNotification,
  createInfoNotification,
} from '../utils/notification-factory';

/**
 * Default notification configuration
 */
const DEFAULT_CONFIG: NotificationConfig = {
  position: 'top-right',
  maxVisible: 5,
  animationDuration: 300,
  defaultDuration: 4000,
  stackSpacing: 0.5,
  edgeOffset: {
    top: '1rem',
    right: '1rem',
  },
  zIndex: 9999,
  enableAnimations: true,
  respectReducedMotion: true,
};

/**
 * Create the context
 */
const NotificationContext = createContext<NotificationContextValue | undefined>(undefined);

/**
 * Notification Provider Component
 */
export const NotificationProvider: React.FC<NotificationProviderProps> = ({
  children,
  config: userConfig = {},
}) => {
  // Merge user config with defaults
  const [config, setConfig] = useState<NotificationConfig>({
    ...DEFAULT_CONFIG,
    ...userConfig,
  });

  // Notification state - split into transient and persistent
  const [transientNotifications, setTransientNotifications] = useState<NotificationObject[]>([]);
  const [persistentNotifications, setPersistentNotifications] = useState<NotificationObject[]>([]);
  
  // Backend operation states
  const [isLoadingNotifications, setIsLoadingNotifications] = useState(false);
  const [notificationError, setNotificationError] = useState<Error | null>(null);
  
  // Notification panel state (for adjusting toast positioning)
  const [notificationPanelOpen, setNotificationPanelOpen] = useState(false);

  // Header dropdown state (for adjusting toast positioning)
  const [headerDropdownOpen, setHeaderDropdownOpen] = useState(false);
  const [headerDropdownHeight, setHeaderDropdownHeight] = useState(0);

  // Combined notifications for backward compatibility
  const notifications = useMemo(() => {
    return [...transientNotifications, ...persistentNotifications];
  }, [transientNotifications, persistentNotifications]);

  // Refs for managing queue and animations
  const queueManager = useRef(new NotificationQueueManager());
  const animationController = useRef(
    new AnimationController(
      {
        duration: {
          fast: 200,
          moderate: config.animationDuration || 300,
          slow: 500,
        },
        easing: {
          enter: 'cubic-bezier(0.4, 0, 0.2, 1)',
          exit: 'cubic-bezier(0.4, 0, 1, 1)',
          reposition: 'cubic-bezier(0.4, 0, 0.2, 1)',
        },
        distance: {
          slideIn: '2rem',
          slideOut: '1rem',
        },
      },
      config.respectReducedMotion
    )
  );

  // Timer for auto-dismiss
  const dismissTimers = useRef<Map<string, NodeJS.Timeout>>(new Map());

  /**
   * Update configuration
   */
  const updateConfig = useCallback((newConfig: Partial<NotificationConfig>) => {
    setConfig(prev => ({ ...prev, ...newConfig }));
  }, []);

  /**
   * Add a notification
   */
  const notify = useCallback((partial: Partial<NotificationObject>): string => {
    const notification = createNotification({
      ...partial,
      position: partial.position || config.position,
      persistenceType: partial.persistenceType || 'transient', // Default to transient
    });

    const isTransient = notification.persistenceType === 'transient';
    
    if (isTransient) {
      // Transient: Add to transient queue for toast display
      const updatedQueue = queueManager.current.add(notification);
      setTransientNotifications(updatedQueue);

      // Set up auto-dismiss timer if duration is specified
      if (notification.duration && notification.duration > 0) {
        const timerId = setTimeout(() => {
          dismiss(notification.id);
        }, notification.duration);
        
        dismissTimers.current.set(notification.id, timerId);
      }

      // Trigger enter animation
      setTimeout(() => {
        updateAnimationState(notification.id, 'idle');
      }, 50);
    } else {
      // Persistent: Add to persistent notifications
      // TODO: POST to backend API when implemented
      // For now, store locally
      
      // Start with entering animation state (same as portal)
      const persistentNotification = {
        ...notification,
        animationState: 'entering' as const,
      };
      
      setPersistentNotifications(prev => [persistentNotification, ...prev]);
      
      // Transition to idle after animation
      setTimeout(() => {
        setPersistentNotifications(prev =>
          prev.map(n => n.id === notification.id ? { ...n, animationState: 'idle' as const } : n)
        );
      }, 300);
      
      // Only show toast if notification panel is CLOSED
      // If panel is open, notification appears in panel only (no toast popup)
      if (!notificationPanelOpen) {
        // Show as transient toast for immediate feedback
        const toastNotification = createNotification({
          ...partial,
          persistenceType: 'transient',
          position: partial.position || config.position,
        });
        
        const updatedQueue = queueManager.current.add(toastNotification);
        setTransientNotifications(updatedQueue);
        
        // Set up auto-dismiss timer for toast
        if (toastNotification.duration && toastNotification.duration > 0) {
          const timerId = setTimeout(() => {
            dismiss(toastNotification.id);
          }, toastNotification.duration);
          
          dismissTimers.current.set(toastNotification.id, timerId);
        }

        // Trigger enter animation
        setTimeout(() => {
          updateAnimationState(toastNotification.id, 'idle');
        }, 50);
      }
    }

    return notification.id;
  }, [config.position, notificationPanelOpen]);

  /**
   * Update animation state
   */
  const updateAnimationState = useCallback((id: string, state: AnimationState) => {
    const updatedQueue = queueManager.current.updateAnimationState(id, state);
    setTransientNotifications(updatedQueue);

    // If exiting, remove after animation completes
    if (state === 'exiting') {
      setTimeout(() => {
        const finalQueue = queueManager.current.updateAnimationState(id, 'exited');
        setTransientNotifications(finalQueue);
        
        // Actually remove from queue after exited
        setTimeout(() => {
          const cleanedQueue = queueManager.current.remove(id);
          setTransientNotifications(cleanedQueue);
          
          // Clear dismiss timer
          const timer = dismissTimers.current.get(id);
          if (timer) {
            clearTimeout(timer);
            dismissTimers.current.delete(id);
          }
        }, 100);
      }, config.animationDuration || 300);
    }
  }, [config.animationDuration]);

  /**
   * Dismiss a notification
   */
  const dismiss = useCallback((id: string) => {
    updateAnimationState(id, 'exiting');
  }, [updateAnimationState]);

  /**
   * Dismiss all notifications
   */
  const dismissAll = useCallback(() => {
    notifications.forEach(notification => {
      dismiss(notification.id);
    });
  }, [notifications, dismiss]);

  /**
   * Update a notification
   */
  const update = useCallback((id: string, updates: Partial<NotificationObject>) => {
    const updatedQueue = queueManager.current.update(id, updates);
    setTransientNotifications(updatedQueue);

    // Also check persistent notifications
    setPersistentNotifications(prev => 
      prev.map(n => n.id === id ? { ...n, ...updates } : n)
    );

    // Update duration timer if needed
    if (updates.duration !== undefined) {
      // Clear existing timer
      const existingTimer = dismissTimers.current.get(id);
      if (existingTimer) {
        clearTimeout(existingTimer);
        dismissTimers.current.delete(id);
      }

      // Set new timer if duration is specified
      if (updates.duration && updates.duration > 0) {
        const timerId = setTimeout(() => {
          dismiss(id);
        }, updates.duration);
        dismissTimers.current.set(id, timerId);
      }
    }
  }, [dismiss]);

  /**
   * Convenience methods
   */
  const success = useCallback((title: string, options?: Partial<NotificationObject>): string => {
    return notify(createSuccessNotification(title, options));
  }, [notify]);

  const error = useCallback((title: string, options?: Partial<NotificationObject>): string => {
    return notify(createErrorNotification(title, options));
  }, [notify]);

  const warning = useCallback((title: string, options?: Partial<NotificationObject>): string => {
    return notify(createWarningNotification(title, options));
  }, [notify]);

  const info = useCallback((title: string, options?: Partial<NotificationObject>): string => {
    return notify(createInfoNotification(title, options));
  }, [notify]);

  /**
   * Persistent notification methods
   */
  
  /**
   * Shorthand for creating a persistent notification
   */
  const notifyPersistent = useCallback((partial: Partial<NotificationObject>): string => {
    return notify({
      ...partial,
      persistenceType: 'persistent',
    });
  }, [notify]);

  /**
   * Mark a persistent notification as read
   * TODO: Call backend API when implemented
   */
  const markAsRead = useCallback(async (id: string): Promise<void> => {
    try {
      setIsLoadingNotifications(true);
      setNotificationError(null);
      
      // TODO: await notificationApi.markAsRead(id);
      
      // Update local state optimistically
      setPersistentNotifications(prev =>
        prev.map(n => n.id === id ? { ...n, isRead: true } : n)
      );
      
      setIsLoadingNotifications(false);
    } catch (error) {
      setNotificationError(error as Error);
      setIsLoadingNotifications(false);
      throw error;
    }
  }, []);

  /**
   * Mark all persistent notifications as read
   * TODO: Call backend API when implemented
   */
  const markAllAsRead = useCallback(async (): Promise<void> => {
    try {
      setIsLoadingNotifications(true);
      setNotificationError(null);
      
      // TODO: await notificationApi.markAllAsRead();
      
      // Update local state optimistically
      setPersistentNotifications(prev =>
        prev.map(n => ({ ...n, isRead: true }))
      );
      
      setIsLoadingNotifications(false);
    } catch (error) {
      setNotificationError(error as Error);
      setIsLoadingNotifications(false);
      throw error;
    }
  }, []);

  /**
   * Clear all persistent notifications
   * TODO: Call backend API when implemented
   */
  const clearHistory = useCallback(async (): Promise<void> => {
    try {
      setIsLoadingNotifications(true);
      setNotificationError(null);
      
      // TODO: await notificationApi.clearAll();
      
      // Animate all notifications out first
      setPersistentNotifications(prev => 
        prev.map(n => ({ ...n, animationState: 'exiting' as const }))
      );
      
      // Wait for animation to complete (300ms), then clear
      setTimeout(() => {
        setPersistentNotifications([]);
        setIsLoadingNotifications(false);
      }, 300);
      
    } catch (error) {
      setNotificationError(error as Error);
      setIsLoadingNotifications(false);
      throw error;
    }
  }, []);

  /**
   * Delete a single persistent notification
   * TODO: Call backend API when implemented
   */
  const deleteNotification = useCallback(async (id: string): Promise<void> => {
    try {
      setIsLoadingNotifications(true);
      setNotificationError(null);
      
      // TODO: await notificationApi.delete(id);
      
      // Animate out first
      setPersistentNotifications(prev =>
        prev.map(n => n.id === id ? { ...n, animationState: 'exiting' as const } : n)
      );
      
      // Wait for animation, then remove
      setTimeout(() => {
        setPersistentNotifications(prev => prev.filter(n => n.id !== id));
        setIsLoadingNotifications(false);
      }, 300);
      
    } catch (error) {
      setNotificationError(error as Error);
      setIsLoadingNotifications(false);
      throw error;
    }
  }, []);

  /**
   * Get count of unread persistent notifications
   */
  const getUnreadCount = useCallback((): number => {
    return persistentNotifications.filter(n => !n.isRead).length;
  }, [persistentNotifications]);

  /**
   * Process expired notifications
   */
  useEffect(() => {
    const interval = setInterval(() => {
      const currentTime = Date.now();
      const expiredIds = queueManager.current.processExpired(currentTime);
      
      expiredIds.forEach(id => {
        dismiss(id);
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [dismiss]);

  /**
   * Clean up on unmount
   */
  useEffect(() => {
    return () => {
      // Clear all timers
      dismissTimers.current.forEach(timer => clearTimeout(timer));
      dismissTimers.current.clear();
      
      // Destroy animation controller
      animationController.current.destroy();
    };
  }, []);

  /**
   * Debug log for persistent notifications
   */
  // Removed console.log for production

  /**
   * Context value
   */
  const contextValue = useMemo<NotificationContextValue>(() => ({
    notifications,
    transientNotifications,
    persistentNotifications,
    isLoadingNotifications,
    notificationError,
    config,
    updateConfig,
    notify,
    success,
    error,
    warning,
    info,
    dismiss,
    dismissAll,
    update,
    notifyPersistent,
    markAsRead,
    markAllAsRead,
    clearHistory,
    deleteNotification,
    getUnreadCount,
    notificationPanelOpen,
    setNotificationPanelOpen,
    headerDropdownOpen,
    setHeaderDropdownOpen,
    headerDropdownHeight,
    setHeaderDropdownHeight,
  }), [
    notifications,
    transientNotifications,
    persistentNotifications,
    isLoadingNotifications,
    notificationError,
    config,
    updateConfig,
    notify,
    success,
    error,
    warning,
    info,
    dismiss,
    dismissAll,
    update,
    notifyPersistent,
    markAsRead,
    markAllAsRead,
    clearHistory,
    deleteNotification,
    getUnreadCount,
    notificationPanelOpen,
    headerDropdownOpen,
    headerDropdownHeight,
  ]);

  return (
    <NotificationContext.Provider value={contextValue}>
      {children}
    </NotificationContext.Provider>
  );
};

/**
 * Hook to use notification context
 */
export const useNotificationContext = (): NotificationContextValue => {
  const context = useContext(NotificationContext);
  
  if (!context) {
    throw new Error('useNotificationContext must be used within a NotificationProvider');
  }
  
  return context;
};

/**
 * Export context for advanced usage
 */
export { NotificationContext };
